import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiDownload, FiEdit3, FiPlus, FiSave, FiTrash2, FiUpload, FiX } from "react-icons/fi";
import { EMPTY_EMPLOYEE_PROFILE } from "../api/hrmNormalizers";
import ComponentLoader from "../components/ComponentLoader";
import ScreenHeader from "./ScreenHeader";
import ScreenMetric from "./ScreenMetric";

const LOCKED_FIELDS = new Set(["Legal Name", "Date of Birth", "Designation", "Work Email"]);
const PROFILE_FIELD_KEYS = {
  "Preferred Name": "preferredName",
  "Blood Group": "bloodGroup",
  Gender: "gender",
  Nationality: "nationality",
  Mobile: "mobile",
  "Personal Email": "personalEmail",
  "LinkedIn Profile": "linkedinProfile",
  "Trailblazer Profile": "trailblazerProfile",
};
const RELATIONSHIP_OPTIONS = ["Self", "Father", "Mother", "Spouse", "Wife", "Husband", "Son", "Daughter", "Guardian", "Friend", "Other"];
const INSURANCE_RELATIONSHIP_OPTIONS = ["Self", "Father", "Mother", "Wife", "Husband", "Son", "Daughter"];
const ADDRESS_TYPES = ["Mailing Address", "Current Living Address"];
const ADDRESS_FIELDS = ["line1", "city", "state", "country", "zipCode"];
const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];
const INSURANCE_COVERAGE_OPTIONS = ["Health Insurance", "Medical", "Medical and Accidental", "Accidental", "Not Applicable"];
// CRM reads and reconciles at most ten active emergency contacts.
const MAX_EMERGENCY_CONTACTS = 10;
const isValidPhone = (value) => /^\+?[\d\s().-]+$/.test(value) && /^\d{7,15}$/.test(value.replace(/\D/g, ""));
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const SECTION_PROFILE_KEYS = {
  personal: "personal",
  contact: "contact",
  payroll: "payrollIdentifiers",
  bank: "bankAccounts",
  insurance: "insuranceDependents",
  emergency: "emergencyContacts",
  addresses: "addresses",
};

const normalizeAddressType = (value) => {
  const text = String(value || "").trim().toLowerCase();
  return text === "other" || text.includes("current") ? "Current Living Address" : "Mailing Address";
};
const normalizeAddressRow = (address = {}, type = "Mailing Address") => ({
  id: address.id || type.toLowerCase().replace(/\s+/g, "-"),
  type: normalizeAddressType(address.type || type),
  line1: address.line1 || address.street || "",
  city: address.city || "",
  state: address.state || "",
  country: address.country || "",
  zipCode: address.zipCode || "",
});
const ensureProfileAddresses = (items = []) => {
  const normalized = items.map((address) => normalizeAddressRow(address));
  return ADDRESS_TYPES.map((type) => normalized.find((address) => address.type === type) || normalizeAddressRow({ type }, type));
};
const getAddressByType = (addresses, type) => addresses.find((address) => address.type === type) || normalizeAddressRow({ type }, type);
const areAddressesSame = (addresses = []) => {
  const mailing = getAddressByType(addresses, "Mailing Address");
  const current = getAddressByType(addresses, "Current Living Address");
  return ADDRESS_FIELDS.some((field) => mailing[field] || current[field]) && ADDRESS_FIELDS.every((field) => String(mailing[field] || "") === String(current[field] || ""));
};
const copyMailingToCurrent = (addresses = []) => {
  const mailing = getAddressByType(addresses, "Mailing Address");
  return ensureProfileAddresses(addresses).map((address) =>
    address.type === "Current Living Address"
      ? { ...address, ...ADDRESS_FIELDS.reduce((payload, field) => ({ ...payload, [field]: mailing[field] || "" }), {}) }
      : address
  );
};
const isProfileLinkLabel = (label) => label === "LinkedIn Profile" || label === "Trailblazer Profile";
const getCrmRecordId = (value) => {
  const id = String(value || "").trim();
  return /^[a-zA-Z0-9]{15,18}$/.test(id) ? id : "";
};
const normalizeDocumentKey = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

const createBankAccount = () => ({
  id: `bank-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  bankName: "",
  accountHolder: "",
  accountNumber: "",
  ifsc: "",
  accountType: "Salary",
  status: "Active",
});
const createInsuranceDependent = () => ({
  id: `insurance-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  name: "",
  relationship: "Self",
  dateOfBirth: "",
  coverage: "Health Insurance",
  nominee: false,
});
const createEmergencyContact = () => ({
  localId: `emergency-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  name: "",
  relationship: "",
  phone: "",
  email: "",
});

const emergencyContactKey = (contactItem = {}, index) => contactItem.id || contactItem.localId || `emergency-contact-${index}`;
const createCustomDocumentRow = () => ({
  key: `custom-document-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  documentName: "",
});
const isTruthyProfileValue = (value) => value === true || ["true", "yes", "1"].includes(String(value || "").trim().toLowerCase());

const CollapseButton = ({ collapsed, label, onToggle }) => (
  <button
    className="new-hrm-icon-button new-hrm-profile-collapse-button"
    type="button"
    onClick={onToggle}
    aria-expanded={!collapsed}
    aria-label={`${collapsed ? "Expand" : "Collapse"} ${label}`}
    title={`${collapsed ? "Expand" : "Collapse"} ${label}`}
  >
    <FiChevronDown className={collapsed ? "" : "new-hrm-profile-collapse-button__icon--open"} aria-hidden="true" />
  </button>
);

const ProfileSectionHeader = ({ canEdit = true, collapsed = false, editing, note, onCancel, onEdit, onSave, onToggle, saving = false, title }) => {
  const showEditControls = Boolean(canEdit && onEdit && onSave);
  return (
    <header className="new-hrm-profile-section-header">
      <div className="new-hrm-profile-section-title">
        <h2>{title}</h2>
        {note && <span>{note}</span>}
      </div>
      {(showEditControls || onToggle) && (
        <div className="new-hrm-profile-section-actions">
          {showEditControls && (
            editing ? (
              <>
                {onCancel && (
                  <button className="new-hrm-secondary-button new-hrm-profile-cancel-button" type="button" onClick={onCancel} disabled={saving}>
                    <FiX aria-hidden="true" />
                    Cancel
                  </button>
                )}
                <button className="new-hrm-primary-button" type="button" onClick={onSave} disabled={saving}>
                  <FiSave aria-hidden="true" />
                  {saving ? "Saving" : "Save"}
                </button>
              </>
            ) : (
              <button className="new-hrm-icon-button new-hrm-profile-edit-button" type="button" onClick={onEdit} aria-label={`Edit ${title}`} title={`Edit ${title}`} disabled={saving}>
                <FiEdit3 aria-hidden="true" />
              </button>
            )
          )}
          {onToggle && <CollapseButton collapsed={collapsed} label={title} onToggle={onToggle} />}
        </div>
      )}
    </header>
  );
};

const RelationshipSelect = ({ allowPlaceholder = false, onChange, options = RELATIONSHIP_OPTIONS, value }) => {
  const normalizedValue = options.includes(value) ? value : "";
  return (
    <select value={normalizedValue} onChange={(event) => onChange(event.target.value)}>
      {allowPlaceholder && <option value="">Select relationship</option>}
      {options.map((option) => (
        <option key={option}>{option}</option>
      ))}
    </select>
  );
};

const DetailList = ({ editing, items, onChange }) => {
  if (!editing) {
    return (
      <dl className="new-hrm-profile-details">
        {items.map((item) => (
          <div key={item.label}>
            <dt>{item.label}</dt>
            <dd>
              {isProfileLinkLabel(item.label) && item.value ? (
                <a href={item.value} target="_blank" rel="noreferrer">
                  {item.value}
                </a>
              ) : (
                item.value || "-"
              )}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <div className="new-hrm-profile-edit-grid">
      {items.map((item, index) => {
        const isLocked = LOCKED_FIELDS.has(item.label);
        return (
          <label key={item.label} className={`new-hrm-profile-field ${isLocked ? "new-hrm-profile-field--locked" : ""}`}>
            <span>{item.label}</span>
            {item.label === "Gender" ? (
              <select disabled={isLocked} value={GENDER_OPTIONS.includes(item.value) ? item.value : ""} onChange={(event) => onChange(index, event.target.value)}>
                <option value="">Select gender</option>
                {GENDER_OPTIONS.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input
                disabled={isLocked}
                type={isProfileLinkLabel(item.label) ? "url" : "text"}
                value={item.value}
                onChange={(event) => onChange(index, event.target.value)}
              />
            )}
          </label>
        );
      })}
    </div>
  );
};

const detailItemsToPayload = (items = []) =>
  items.reduce((payload, item) => {
    const key = PROFILE_FIELD_KEYS[item.label];
    if (key) {
      payload[key] = item.value;
    }
    return payload;
  }, {});

const ProfileScreen = ({
  loading = false,
  loadingLabel = "Refreshing profile",
  onDocumentDelete,
  onDocumentUpload,
  onProfileSave,
  profile: profileInput = EMPTY_EMPLOYEE_PROFILE,
  saveBlocked = false,
}) => {
  const profileData = profileInput && typeof profileInput === "object" ? profileInput : EMPTY_EMPLOYEE_PROFILE;
  const [personal, setPersonal] = useState(profileData.personal || []);
  const [contact, setContact] = useState(profileData.contact || []);
  const [payrollIdentifiers, setPayrollIdentifiers] = useState(profileData.payrollIdentifiers || []);
  const [bankAccounts, setBankAccounts] = useState(profileData.bankAccounts || []);
  const [insuranceDependents, setInsuranceDependents] = useState(profileData.insuranceDependents || []);
  const [deletedInsuranceRelatedIds, setDeletedInsuranceRelatedIds] = useState([]);
  const [emergencyContacts, setEmergencyContacts] = useState(profileData.emergencyContacts || []);
  const [addresses, setAddresses] = useState(ensureProfileAddresses(profileData.addresses || []));
  const [sameAsMailing, setSameAsMailing] = useState(false);
  const [customDocumentRows, setCustomDocumentRows] = useState([]);
  const [saveMessage, setSaveMessage] = useState("");
  const [savingSection, setSavingSection] = useState("");
  const pendingOperationRef = useRef(null);
  const savedProfileRef = useRef(profileData);
  const editingSectionsRef = useRef({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [editingSections, setEditingSections] = useState({
    personal: false,
    contact: false,
    payroll: false,
    bank: false,
    insurance: false,
    emergency: false,
    addresses: false,
  });

  const summary = { ...EMPTY_EMPLOYEE_PROFILE.summary, ...(profileData.summary || {}) };
  const profileIdentity = `${summary.employeeId}|${(profileData.contact || []).find((item) => item.label === "Work Email")?.value || ""}`;
  const previousIdentityRef = useRef(profileIdentity);
  const completionSuggestions = Array.isArray(summary.completionSuggestions) ? summary.completionSuggestions : [];
  const documentUploadConfig = profileData.documentUpload || {};
  const customDocumentsAllowed = Boolean(
    documentUploadConfig.allowAdditionalDocuments ||
      documentUploadConfig.customDocumentsAllowed ||
      documentUploadConfig.onboarding ||
      documentUploadConfig.allowDocumentUpload
  );
  const documentRows = useMemo(() => {
    const requirements = profileData.documentUploadRequirements || profileData.documentUpload?.requiredDocuments || [];
    const existingDocuments = profileData.documents || [];
    const existingDocumentRows = existingDocuments.map((document, index) => ({
      key: document.id || document.label || `document-${index}`,
      documentName: document.documentName || document.label || `Document ${index + 1}`,
      status: document.status || "Submitted",
      uploaded: true,
      canUpload: customDocumentsAllowed,
      canDelete: Boolean(document.canDelete),
      document,
      downloadUrl: document.downloadUrl || document.fileUrl,
    }));
    const customRows = customDocumentRows.map((row) => ({
      key: row.key,
      documentName: row.documentName,
      status: "Pending",
      uploaded: false,
      canUpload: true,
      canDelete: false,
      custom: true,
      document: { documentName: row.documentName },
      downloadUrl: "",
    }));

    if (requirements.length) {
      const requirementRows = requirements.map((requirement, index) => {
        const uploadedDocument = requirement.document || {};
        const documentName = requirement.documentName || requirement.name || uploadedDocument.documentName || uploadedDocument.label || `Document ${index + 1}`;
        const document = {
          ...uploadedDocument,
          id: requirement.documentId || requirement.externalAttachmentId || uploadedDocument.externalAttachmentId || uploadedDocument.id,
          externalAttachmentId: requirement.externalAttachmentId || requirement.documentId || uploadedDocument.externalAttachmentId || uploadedDocument.id,
          documentName,
          label: uploadedDocument.label || documentName,
          downloadUrl: requirement.downloadUrl || uploadedDocument.downloadUrl || uploadedDocument.fileUrl,
          fileUrl: requirement.fileUrl || uploadedDocument.fileUrl || uploadedDocument.downloadUrl,
          canDelete: requirement.canDelete || uploadedDocument.canDelete,
        };
        const uploaded = Boolean(requirement.uploaded || document.id);
        return {
          key: requirement.id || documentName,
          documentName,
          status: requirement.status || (uploaded ? "Submitted" : "Pending"),
          uploaded,
          canUpload: requirement.canUpload !== false,
          canDelete: Boolean(uploaded && document.canDelete),
          document,
          downloadUrl: document.downloadUrl || document.fileUrl,
        };
      });
      const requirementKeys = new Set(requirementRows.map((row) => normalizeDocumentKey(row.documentName)));
      const extraExistingRows = existingDocumentRows.filter((row) => !requirementKeys.has(normalizeDocumentKey(row.documentName)));
      return [...requirementRows, ...extraExistingRows, ...customRows];
    }

    return [...existingDocumentRows, ...customRows];
  }, [customDocumentRows, customDocumentsAllowed, profileData]);
  const documentUploadEnabled = Boolean(documentUploadConfig.enabled || documentRows.some((row) => row.canUpload));
  const hrmPermissions = profileData.hrmPermissions || {};
  const editPermissions = profileData.editPermissions || {};
  const lockedFields = new Set(profileData.lockedFields || []);
  const onboardingProfileEditable = Boolean(hrmPermissions.onboarding || documentUploadConfig.onboarding);
  const canEditPayrollIdentifiers = Boolean(
    editPermissions.payrollIdentifiers ||
      hrmPermissions.payrollIdentifiersEditable ||
      (onboardingProfileEditable && !lockedFields.has("payrollIdentifiers"))
  );
  const canEditBankAccounts = Boolean(
    editPermissions.bankAccounts ||
      hrmPermissions.bankAccountsEditable ||
      (onboardingProfileEditable && !lockedFields.has("bankAccounts"))
  );
  const canEditInsuranceRelated = Boolean(
    editPermissions.insuranceRelated !== false &&
      editPermissions.insuranceRelatedEditable !== false &&
      hrmPermissions.insuranceRelatedEditable !== false &&
      !lockedFields.has("insuranceRelated")
  );
  const sectionLabels = useMemo(
    () => ({
      personal: "Personal details",
      contact: "Contact details",
      payroll: "Payroll identifiers",
      bank: "Bank details",
      insurance: "Insurance related records",
      emergency: "Emergency contacts",
      addresses: "Addresses",
    }),
    []
  );

  useEffect(() => {
    const identityChanged = previousIdentityRef.current !== profileIdentity;
    previousIdentityRef.current = profileIdentity;
    savedProfileRef.current = profileData;
    const editing = identityChanged ? {} : { ...editingSectionsRef.current };
    if (!canEditPayrollIdentifiers) editing.payroll = false;
    if (!canEditBankAccounts) editing.bank = false;
    if (!canEditInsuranceRelated) editing.insurance = false;
    editingSectionsRef.current = editing;
    setEditingSections(editing);
    // A section save refreshes the whole profile; keep other open drafts intact.
    if (!editing.personal) setPersonal(profileData.personal || []);
    if (!editing.contact) setContact(profileData.contact || []);
    if (!editing.payroll) setPayrollIdentifiers(profileData.payrollIdentifiers || []);
    if (!editing.bank) setBankAccounts(profileData.bankAccounts || []);
    if (!editing.insurance) {
      setInsuranceDependents(profileData.insuranceDependents || []);
      setDeletedInsuranceRelatedIds([]);
    }
    if (!editing.emergency) setEmergencyContacts(profileData.emergencyContacts || []);
    if (!editing.addresses) {
      const nextAddresses = ensureProfileAddresses(profileData.addresses || []);
      setAddresses(nextAddresses);
      setSameAsMailing(areAddressesSame(nextAddresses));
    }
    if (identityChanged) {
      pendingOperationRef.current = null;
      setCustomDocumentRows([]);
      setSaveMessage("");
      setSavingSection("");
    }
  }, [profileData, profileIdentity, canEditPayrollIdentifiers, canEditBankAccounts, canEditInsuranceRelated]);

  useEffect(() => () => {
    pendingOperationRef.current = null;
  }, []);

  const setSectionEditing = (section, editing) => {
    editingSectionsRef.current = { ...editingSectionsRef.current, [section]: editing };
    setEditingSections(editingSectionsRef.current);
    if (editing) {
      setCollapsedSections((current) => ({ ...current, [section]: false }));
    }
    setSaveMessage("");
  };

  const isSectionCollapsed = (section) => Boolean(collapsedSections[section]);

  const toggleSectionCollapsed = (section) => {
    setCollapsedSections((current) => ({ ...current, [section]: !current[section] }));
  };

  const handleSaveSection = async (section) => {
    if (saveBlocked || loading || pendingOperationRef.current) return;
    if ((section === "insurance" && !canEditInsuranceRelated) || (section === "bank" && !canEditBankAccounts) || (section === "payroll" && !canEditPayrollIdentifiers)) return;
    if (typeof onProfileSave !== "function") {
      setSaveMessage(`${sectionLabels[section]} cannot be saved right now.`);
      return;
    }
    if (
      section === "insurance" &&
      insuranceDependents.some((dependent) => !INSURANCE_RELATIONSHIP_OPTIONS.includes(dependent.relationship))
    ) {
      setSaveMessage("Select a valid insurance relationship before saving.");
      return;
    }

    const cleanEmergencyContacts = emergencyContacts.map((item) => ({
      ...item,
      name: String(item.name || "").trim(),
      relationship: String(item.relationship || "").trim(),
      phone: String(item.phone || "").trim(),
      email: String(item.email || "").trim(),
    }));
    if (section === "emergency") {
      if (cleanEmergencyContacts.length > MAX_EMERGENCY_CONTACTS) {
        setSaveMessage(`A maximum of ${MAX_EMERGENCY_CONTACTS} emergency contacts can be saved.`);
        return;
      }
      if (!cleanEmergencyContacts.length || cleanEmergencyContacts.some((item) => !item.name || !item.relationship || !item.phone)) {
        setSaveMessage("Enter a name, relationship, and phone number for every emergency contact.");
        return;
      }
      if (cleanEmergencyContacts.some((item) => !RELATIONSHIP_OPTIONS.includes(item.relationship))) {
        setSaveMessage("Select a valid emergency contact relationship before saving.");
        return;
      }
      if (cleanEmergencyContacts.some((item) => !isValidPhone(item.phone))) {
        setSaveMessage("Enter a valid phone number with 7 to 15 digits for every emergency contact.");
        return;
      }
      if (cleanEmergencyContacts.some((item) => item.email && !isValidEmail(item.email))) {
        setSaveMessage("Enter a valid email address or leave email blank for every emergency contact.");
        return;
      }
    }

    const personalPayload = detailItemsToPayload(personal);
    const contactPayload = detailItemsToPayload(contact);
    const payloads = {
      personal: {
        ...personalPayload,
        personal: personalPayload,
      },
      contact: {
        ...contactPayload,
        contact: contactPayload,
      },
      payroll: { payrollIdentifiers },
      bank: { bankAccounts },
      insurance: { insuranceRelated: insuranceDependents, deletedInsuranceRelatedIds },
      emergency: { emergencyContacts: cleanEmergencyContacts },
      addresses: { addresses },
    };

    const operation = { profile: savedProfileRef.current };
    pendingOperationRef.current = operation;
    setSaveMessage("");
    try {
      setSavingSection(section);
      const didSave = await onProfileSave(section, payloads[section]);
      if (pendingOperationRef.current !== operation) return;
      if (didSave !== true) {
        throw new Error(`${sectionLabels[section]} could not be saved.`);
      }
      const values = { personal, contact, payroll: payrollIdentifiers, bank: bankAccounts, insurance: insuranceDependents, emergency: cleanEmergencyContacts, addresses };
      const profileKey = SECTION_PROFILE_KEYS[section];
      if (savedProfileRef.current === operation.profile) {
        savedProfileRef.current = { ...savedProfileRef.current, [profileKey]: values[section] };
      }
      cancelSectionEditing(section);
      setSaveMessage(`${sectionLabels[section]} saved.`);
    } catch (error) {
      if (pendingOperationRef.current === operation) {
        setSaveMessage(error?.message || `${sectionLabels[section]} could not be saved.`);
      }
    } finally {
      if (pendingOperationRef.current === operation) {
        pendingOperationRef.current = null;
        setSavingSection("");
      }
    }
  };

  const cancelSectionEditing = (section) => {
    const setters = { personal: setPersonal, contact: setContact, payroll: setPayrollIdentifiers, bank: setBankAccounts, insurance: setInsuranceDependents, emergency: setEmergencyContacts, addresses: setAddresses };
    const values = savedProfileRef.current[SECTION_PROFILE_KEYS[section]] || [];
    setters[section](section === "addresses" ? ensureProfileAddresses(values) : values);
    if (section === "addresses") setSameAsMailing(areAddressesSame(values));
    if (section === "insurance") setDeletedInsuranceRelatedIds([]);
    setSectionEditing(section, false);
  };

  const updateField = (setter) => (index, value) => {
    setter((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, value } : item)));
    setSaveMessage("");
  };

  const updateBankAccount = (index, field, value) => {
    setBankAccounts((current) =>
      current.map((account, accountIndex) => (accountIndex === index ? { ...account, [field]: value } : account))
    );
    setSaveMessage("");
  };

  const addBankAccount = () => {
    setBankAccounts((current) => [...current, createBankAccount()]);
    setSaveMessage("");
  };

  const removeUnsavedBankAccount = (account) => {
    if (getCrmRecordId(account?.id)) {
      return;
    }
    setBankAccounts((current) => current.filter((item) => item.id !== account.id));
    setSaveMessage("");
  };

  const updateInsuranceDependent = (index, field, value) => {
    setInsuranceDependents((current) =>
      current.map((dependent, dependentIndex) => (dependentIndex === index ? { ...dependent, [field]: value } : dependent))
    );
    setSaveMessage("");
  };

  const addInsuranceDependent = () => {
    setInsuranceDependents((current) => [...current, createInsuranceDependent()]);
    setSaveMessage("");
  };

  const removeInsuranceDependent = (dependent) => {
    const recordId = getCrmRecordId(dependent?.id);
    if (recordId) {
      setDeletedInsuranceRelatedIds((current) => (current.includes(recordId) ? current : [...current, recordId]));
    }
    setInsuranceDependents((current) => current.filter((item) => item.id !== dependent.id));
    setSaveMessage("");
  };

  const updateEmergencyContact = (index, field, value) => {
    setEmergencyContacts((current) =>
      current.map((contactItem, contactIndex) => (contactIndex === index ? { ...contactItem, [field]: value } : contactItem))
    );
    setSaveMessage("");
  };

  const beginEmergencyEditing = () => {
    setEmergencyContacts((current) => (current.length ? current : [createEmergencyContact()]));
    setSectionEditing("emergency", true);
  };

  const addEmergencyContact = () => {
    setEmergencyContacts((current) => current.length < MAX_EMERGENCY_CONTACTS ? [...current, createEmergencyContact()] : current);
    setSaveMessage("");
  };

  const isUnsavedEmergencyContact = (contactItem) => Boolean(
    contactItem.localId && !(savedProfileRef.current.emergencyContacts || []).some((item) => item.localId === contactItem.localId)
  );

  const removeUnsavedEmergencyContact = (contactItem) => {
    if (!isUnsavedEmergencyContact(contactItem)) return;
    setEmergencyContacts((current) => current.filter((item) => emergencyContactKey(item) !== emergencyContactKey(contactItem)));
    setSaveMessage("");
  };

  const updateAddress = (index, field, value) => {
    setAddresses((current) => {
      const next = current.map((addressItem, addressIndex) => (addressIndex === index ? { ...addressItem, [field]: value } : addressItem));
      if (sameAsMailing && next[index]?.type === "Mailing Address") {
        return copyMailingToCurrent(next);
      }
      return next;
    });
    if (sameAsMailing && addresses[index]?.type === "Current Living Address") {
      setSameAsMailing(false);
    }
    setSaveMessage("");
  };

  const toggleSameAsMailing = (checked) => {
    setSameAsMailing(checked);
    if (checked) {
      setAddresses((current) => copyMailingToCurrent(current));
    }
    setSaveMessage("");
  };

  const addCustomDocumentRow = () => {
    setCustomDocumentRows((current) => [...current, createCustomDocumentRow()]);
    setCollapsedSections((current) => ({ ...current, documents: false }));
    setSaveMessage("");
  };

  const updateCustomDocumentName = (key, value) => {
    setCustomDocumentRows((current) => current.map((row) => (row.key === key ? { ...row, documentName: value } : row)));
    setSaveMessage("");
  };

  const removeCustomDocumentRow = (key) => {
    setCustomDocumentRows((current) => current.filter((row) => row.key !== key));
    setSaveMessage("");
  };

  const handleDocumentUpload = async (documentName, file, rowKey = "") => {
    if (!file || !onDocumentUpload || saveBlocked || loading || pendingOperationRef.current) {
      return;
    }
    const cleanDocumentName = String(documentName || "").trim();
    if (!cleanDocumentName) {
      setSaveMessage("Enter a document name before uploading.");
      return;
    }

    const operation = {};
    pendingOperationRef.current = operation;
    setSaveMessage("");
    try {
      setSavingSection("documents");
      const didSave = await onDocumentUpload(cleanDocumentName, file);
      if (pendingOperationRef.current !== operation) return;
      if (didSave !== true) {
        throw new Error(`${cleanDocumentName} could not be uploaded.`);
      }
      if (rowKey) {
        removeCustomDocumentRow(rowKey);
      }
      setSaveMessage(`${cleanDocumentName} uploaded.`);
    } catch (error) {
      if (pendingOperationRef.current === operation) {
        setSaveMessage(error?.message || `${cleanDocumentName} could not be uploaded.`);
      }
    } finally {
      if (pendingOperationRef.current === operation) {
        pendingOperationRef.current = null;
        setSavingSection("");
      }
    }
  };

  const handleDocumentDelete = async (row) => {
    if (!onDocumentDelete || saveBlocked || loading || pendingOperationRef.current) {
      return;
    }

    const operation = {};
    pendingOperationRef.current = operation;
    setSaveMessage("");
    try {
      setSavingSection("documents");
      const didDelete = await onDocumentDelete(row.document);
      if (pendingOperationRef.current !== operation) return;
      if (didDelete !== true) {
        throw new Error(`${row.documentName} could not be deleted.`);
      }
      setSaveMessage(`${row.documentName} deleted.`);
    } catch (error) {
      if (pendingOperationRef.current === operation) {
        setSaveMessage(error?.message || `${row.documentName} could not be deleted.`);
      }
    } finally {
      if (pendingOperationRef.current === operation) {
        pendingOperationRef.current = null;
        setSavingSection("");
      }
    }
  };

  return (
    <section className="new-hrm-screen new-hrm-profile-screen new-hrm-loader-host">
      <ComponentLoader active={loading} label={loadingLabel} />
      <ScreenHeader
        eyebrow="Employee Record"
        title="My Profile"
        description="Update employee-owned details and maintain bank and insurance records."
      />

      {saveMessage && <div className="new-hrm-data-banner" role="status">{saveMessage}</div>}

      <div className="new-hrm-screen-metrics">
        <ScreenMetric label="Employee ID" value={summary.employeeId} detail={summary.department} />
        <ScreenMetric label="Work Location" value={summary.location} detail={`Joined ${summary.joinedOn}`} />
        <ScreenMetric label="Profile Complete" value={summary.completeness} detail="Fields completed" />
      </div>

      {completionSuggestions.length > 0 && (
        <section className="new-hrm-panel new-hrm-profile-completion new-hrm-profile-collapsible-card">
          <header>
            <div>
              <h2>Complete Your Profile</h2>
              <p>Fix these items to reach 100%.</p>
            </div>
            <div className="new-hrm-profile-section-actions">
              <strong>{summary.completeness}</strong>
              <CollapseButton
                collapsed={isSectionCollapsed("completion")}
                label="Complete Your Profile"
                onToggle={() => toggleSectionCollapsed("completion")}
              />
            </div>
          </header>
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("completion")}>
            <ul>
              {completionSuggestions.map((suggestion) => (
                <li key={suggestion}>{suggestion}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <fieldset
        className="new-hrm-screen-grid new-hrm-screen-grid--two new-hrm-profile-grid"
        disabled={saveBlocked || loading || Boolean(savingSection)}
        aria-label="Profile details"
        aria-busy={loading || Boolean(savingSection)}
        style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
      >
        <section aria-label="Personal Details" className="new-hrm-panel new-hrm-profile-collapsible-card new-hrm-loader-host">
          <ComponentLoader active={savingSection === "personal"} label="Saving personal details" />
          <ProfileSectionHeader
            title="Personal Details"
            note="Legal name, DOB, designation, and work email are HR-controlled."
            collapsed={isSectionCollapsed("personal")}
            editing={editingSections.personal}
            onCancel={() => cancelSectionEditing("personal")}
            onEdit={() => setSectionEditing("personal", true)}
            onSave={() => handleSaveSection("personal")}
            onToggle={() => toggleSectionCollapsed("personal")}
            saving={savingSection === "personal"}
          />
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("personal")}>
            <DetailList editing={editingSections.personal} items={personal} onChange={updateField(setPersonal)} />
          </div>
        </section>

        <section aria-label="Contact Details" className="new-hrm-panel new-hrm-profile-collapsible-card new-hrm-loader-host">
          <ComponentLoader active={savingSection === "contact"} label="Saving contact details" />
          <ProfileSectionHeader
            title="Contact Details"
            note="Work email is locked. Personal contact fields are editable."
            collapsed={isSectionCollapsed("contact")}
            editing={editingSections.contact}
            onCancel={() => cancelSectionEditing("contact")}
            onEdit={() => setSectionEditing("contact", true)}
            onSave={() => handleSaveSection("contact")}
            onToggle={() => toggleSectionCollapsed("contact")}
            saving={savingSection === "contact"}
          />
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("contact")}>
            <DetailList editing={editingSections.contact} items={contact} onChange={updateField(setContact)} />
          </div>
        </section>

        <section aria-label="Employment Details" className="new-hrm-panel new-hrm-profile-collapsible-card new-hrm-loader-host">
          <ProfileSectionHeader
            canEdit={false}
            title="Employment Details"
            note="Employment details are HR-controlled and cannot be edited here."
            collapsed={isSectionCollapsed("employment")}
            onToggle={() => toggleSectionCollapsed("employment")}
          />
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("employment")}>
            <DetailList editing={false} items={profileData.employment || []} />
          </div>
        </section>

        <section aria-label="Payroll Identifiers" className="new-hrm-panel new-hrm-profile-collapsible-card">
          <ComponentLoader active={savingSection === "payroll"} label="Saving payroll identifiers" />
          <ProfileSectionHeader
            canEdit={canEditPayrollIdentifiers}
            title="Payroll Identifiers"
            note={canEditPayrollIdentifiers ? "Editable while onboarding is enabled." : "Payroll identifiers are finance-controlled and cannot be edited here."}
            collapsed={isSectionCollapsed("payroll")}
            editing={editingSections.payroll}
            onCancel={() => cancelSectionEditing("payroll")}
            onEdit={() => setSectionEditing("payroll", true)}
            onSave={() => handleSaveSection("payroll")}
            onToggle={() => toggleSectionCollapsed("payroll")}
            saving={savingSection === "payroll"}
          />
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("payroll")}>
            <DetailList editing={editingSections.payroll} items={payrollIdentifiers || []} onChange={updateField(setPayrollIdentifiers)} />
          </div>
        </section>

        <section aria-label="Insurance Related" className="new-hrm-panel new-hrm-screen-panel-span new-hrm-profile-collapsible-card new-hrm-loader-host">
          <ComponentLoader active={savingSection === "insurance"} label="Saving insurance records" />
          <ProfileSectionHeader
            canEdit={canEditInsuranceRelated}
            title="Insurance Related"
            note={canEditInsuranceRelated ? "Employees can update insurance dependents while HRM insurance editing is enabled." : "Insurance records are locked in HRM."}
            collapsed={isSectionCollapsed("insurance")}
            editing={editingSections.insurance}
            onCancel={() => cancelSectionEditing("insurance")}
            onEdit={() => setSectionEditing("insurance", true)}
            onSave={() => handleSaveSection("insurance")}
            onToggle={() => toggleSectionCollapsed("insurance")}
            saving={savingSection === "insurance"}
          />
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("insurance")}>
            <div className="new-hrm-profile-table">
              <div className={`new-hrm-profile-table__head new-hrm-profile-table__head--insurance ${editingSections.insurance ? "new-hrm-profile-table__head--insurance-editing" : ""}`}>
                <span>Name</span>
                <span>Relationship</span>
                <span>DOB</span>
                <span>Coverage</span>
                <span>Nominee</span>
                {editingSections.insurance && <span>Action</span>}
              </div>
              {insuranceDependents.map((dependent, index) => (
                <div
                  key={dependent.id}
                  className={`new-hrm-profile-table__row new-hrm-profile-table__row--insurance ${editingSections.insurance ? "new-hrm-profile-table__row--insurance-editing" : "new-hrm-profile-table__row--readonly"}`}
                >
                  {editingSections.insurance ? (
                    <>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Name</span>
                        <input value={dependent.name || ""} onChange={(event) => updateInsuranceDependent(index, "name", event.target.value)} placeholder="Full name" />
                      </label>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Relationship</span>
                        <RelationshipSelect
                          allowPlaceholder
                          options={INSURANCE_RELATIONSHIP_OPTIONS}
                          value={dependent.relationship}
                          onChange={(value) => updateInsuranceDependent(index, "relationship", value)}
                        />
                      </label>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Date of birth</span>
                        <input type="date" value={dependent.dateOfBirth || ""} onChange={(event) => updateInsuranceDependent(index, "dateOfBirth", event.target.value)} />
                      </label>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Coverage</span>
                        <select value={INSURANCE_COVERAGE_OPTIONS.includes(dependent.coverage) ? dependent.coverage : "Health Insurance"} onChange={(event) => updateInsuranceDependent(index, "coverage", event.target.value)}>
                          {INSURANCE_COVERAGE_OPTIONS.map((option) => (
                            <option key={option}>{option}</option>
                          ))}
                        </select>
                      </label>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Nominee</span>
                        <select value={isTruthyProfileValue(dependent.nominee) ? "true" : "false"} onChange={(event) => updateInsuranceDependent(index, "nominee", event.target.value === "true")}>
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      </label>
                      <button className="new-hrm-profile-remove-button" type="button" onClick={() => removeInsuranceDependent(dependent)} aria-label={`Remove ${dependent.name || "insurance dependent"}`}>
                        <FiTrash2 aria-hidden="true" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span data-label="Name">{dependent.name || "-"}</span>
                      <span data-label="Relationship">{dependent.relationship || "-"}</span>
                      <span data-label="Date of birth">{dependent.dateOfBirth || "-"}</span>
                      <span data-label="Coverage">{dependent.coverage || "-"}</span>
                      <span data-label="Nominee">{isTruthyProfileValue(dependent.nominee) ? "Yes" : "No"}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
            {editingSections.insurance && (
              <button className="new-hrm-secondary-button new-hrm-profile-add-button" type="button" onClick={addInsuranceDependent}>
                <FiPlus aria-hidden="true" />
                Add Related
              </button>
            )}
            {!insuranceDependents.length && !editingSections.insurance && <p className="new-hrm-empty-state">No insurance related records available from CRM.</p>}
          </div>
        </section>

        <section aria-label="Bank Details" className="new-hrm-panel new-hrm-screen-panel-span new-hrm-profile-collapsible-card new-hrm-loader-host">
          <ComponentLoader active={savingSection === "bank"} label="Saving bank details" />
          <ProfileSectionHeader
            canEdit={canEditBankAccounts}
            title="Bank Details"
            note={canEditBankAccounts ? "Editable while onboarding is enabled." : "Bank records are finance-controlled and cannot be edited here."}
            collapsed={isSectionCollapsed("bank")}
            editing={editingSections.bank}
            onCancel={() => cancelSectionEditing("bank")}
            onEdit={() => setSectionEditing("bank", true)}
            onSave={() => handleSaveSection("bank")}
            onToggle={() => toggleSectionCollapsed("bank")}
            saving={savingSection === "bank"}
          />
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("bank")}>
            <div className="new-hrm-profile-table">
              <div className="new-hrm-profile-table__head new-hrm-profile-table__head--bank">
                <span>Bank</span>
                <span>Holder</span>
                <span>Account</span>
                <span>IFSC</span>
                <span>Type</span>
                <span>Status</span>
              </div>
              {bankAccounts.map((account, index) => (
                <div key={account.id || `bank-account-${index}`} className={`new-hrm-profile-table__row new-hrm-profile-table__row--bank ${editingSections.bank ? "new-hrm-profile-table__row--bank-editing" : "new-hrm-profile-table__row--readonly"}`}>
                  {editingSections.bank ? (
                    <>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Bank</span>
                        <input value={account.bankName || ""} onChange={(event) => updateBankAccount(index, "bankName", event.target.value)} placeholder="Bank" />
                      </label>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Account holder</span>
                        <input value={account.accountHolder || ""} onChange={(event) => updateBankAccount(index, "accountHolder", event.target.value)} placeholder="Holder" />
                      </label>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Account number</span>
                        <input value={account.accountNumber || ""} onChange={(event) => updateBankAccount(index, "accountNumber", event.target.value)} placeholder="Account number" />
                      </label>
                      <label className="new-hrm-profile-mobile-field">
                        <span>IFSC</span>
                        <input value={account.ifsc || ""} onChange={(event) => updateBankAccount(index, "ifsc", event.target.value.toUpperCase())} placeholder="IFSC" />
                      </label>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Account type</span>
                        <select value={account.accountType || "Salary"} onChange={(event) => updateBankAccount(index, "accountType", event.target.value)}>
                          <option>Salary</option>
                          <option>Savings</option>
                          <option>Current</option>
                        </select>
                      </label>
                      <label className="new-hrm-profile-mobile-field">
                        <span>Status</span>
                        <select value={account.status || "Active"} onChange={(event) => updateBankAccount(index, "status", event.target.value)}>
                          <option>Active</option>
                          <option>Primary</option>
                        </select>
                      </label>
                      {!getCrmRecordId(account.id) && (
                        <button className="new-hrm-profile-remove-button" type="button" onClick={() => removeUnsavedBankAccount(account)}>
                          <FiTrash2 aria-hidden="true" />
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <span data-label="Bank">{account.bankName || "-"}</span>
                      <span data-label="Account holder">{account.accountHolder || "-"}</span>
                      <span data-label="Account number">{account.accountNumber || "-"}</span>
                      <span data-label="IFSC">{account.ifsc || "-"}</span>
                      <span data-label="Account type">{account.accountType || "-"}</span>
                      <span data-label="Status">{account.status || "-"}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
            {editingSections.bank && (
              <button className="new-hrm-secondary-button new-hrm-profile-add-button" type="button" onClick={addBankAccount}>
                <FiPlus aria-hidden="true" />
                Add Bank Account
              </button>
            )}
            {!bankAccounts.length && !editingSections.bank && <p className="new-hrm-empty-state">No bank details available from CRM.</p>}
          </div>
        </section>

        <section aria-label="Emergency Contacts" className="new-hrm-panel new-hrm-profile-collapsible-card new-hrm-loader-host">
          <ComponentLoader active={savingSection === "emergency"} label="Saving emergency contacts" />
          <ProfileSectionHeader
            title="Emergency Contacts"
            collapsed={isSectionCollapsed("emergency")}
            editing={editingSections.emergency}
            onCancel={() => cancelSectionEditing("emergency")}
            onEdit={beginEmergencyEditing}
            onSave={() => handleSaveSection("emergency")}
            onToggle={() => toggleSectionCollapsed("emergency")}
            saving={savingSection === "emergency"}
          />
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("emergency")}>
            <div className="new-hrm-profile-card-list">
              {emergencyContacts.map((contactItem, index) => (
                <article key={emergencyContactKey(contactItem, index)} className={`new-hrm-profile-card ${editingSections.emergency ? "new-hrm-profile-card--edit" : ""}`}>
                  {editingSections.emergency ? (
                    <>
                      <label className="new-hrm-profile-field">
                        <span>Name</span>
                        <input value={contactItem.name || ""} onChange={(event) => updateEmergencyContact(index, "name", event.target.value)} autoComplete="name" />
                      </label>
                      <label className="new-hrm-profile-field">
                        <span>Relationship</span>
                        <RelationshipSelect allowPlaceholder value={contactItem.relationship} onChange={(value) => updateEmergencyContact(index, "relationship", value)} />
                      </label>
                      <label className="new-hrm-profile-field">
                        <span>Phone</span>
                        <input type="tel" value={contactItem.phone || ""} onChange={(event) => updateEmergencyContact(index, "phone", event.target.value)} autoComplete="tel" />
                      </label>
                      <label className="new-hrm-profile-field">
                        <span>Email</span>
                        <input type="email" value={contactItem.email || ""} onChange={(event) => updateEmergencyContact(index, "email", event.target.value)} autoComplete="email" />
                      </label>
                      {isUnsavedEmergencyContact(contactItem) && emergencyContacts.length > 1 && (
                        <button
                          className="new-hrm-secondary-button new-hrm-profile-remove-contact-button"
                          type="button"
                          onClick={() => removeUnsavedEmergencyContact(contactItem)}
                        >
                          <FiTrash2 aria-hidden="true" />
                          Remove
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <strong>{contactItem.name}</strong>
                      <span>{contactItem.relationship}</span>
                      <p>{contactItem.phone}</p>
                      <small>{contactItem.email}</small>
                    </>
                  )}
                </article>
              ))}
            </div>
            {editingSections.emergency && (
              <button className="new-hrm-secondary-button new-hrm-profile-add-button" type="button" onClick={addEmergencyContact} disabled={emergencyContacts.length >= MAX_EMERGENCY_CONTACTS} title={emergencyContacts.length >= MAX_EMERGENCY_CONTACTS ? `Maximum ${MAX_EMERGENCY_CONTACTS} emergency contacts` : undefined}>
                <FiPlus aria-hidden="true" />
                Add Emergency Contact
              </button>
            )}
            {!emergencyContacts.length && !editingSections.emergency && (
              <div className="new-hrm-profile-empty-action">
                <p className="new-hrm-empty-state">No emergency contact has been provided.</p>
                <button className="new-hrm-secondary-button" type="button" onClick={beginEmergencyEditing}>
                  <FiPlus aria-hidden="true" />
                  Add Emergency Contact
                </button>
              </div>
            )}
          </div>
        </section>

        <section aria-label="Addresses" className="new-hrm-panel new-hrm-profile-collapsible-card new-hrm-loader-host">
          <ComponentLoader active={savingSection === "addresses"} label="Saving addresses" />
          <ProfileSectionHeader
            title="Addresses"
            collapsed={isSectionCollapsed("addresses")}
            editing={editingSections.addresses}
            onCancel={() => cancelSectionEditing("addresses")}
            onEdit={() => setSectionEditing("addresses", true)}
            onSave={() => handleSaveSection("addresses")}
            onToggle={() => toggleSectionCollapsed("addresses")}
            saving={savingSection === "addresses"}
          />
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("addresses")}>
            {editingSections.addresses && (
              <label className="new-hrm-profile-checkbox">
                <input
                  type="checkbox"
                  checked={sameAsMailing}
                  onChange={(event) => toggleSameAsMailing(event.target.checked)}
                />
                <span>Current living address is same as mailing</span>
              </label>
            )}
            <div className="new-hrm-profile-card-list">
              {addresses.map((address, index) => (
                <article key={`${address.type}-${index}`} className={`new-hrm-profile-card ${editingSections.addresses ? "new-hrm-profile-card--edit" : ""}`}>
                  {editingSections.addresses ? (
                    <>
                      <strong>{normalizeAddressType(address.type)}</strong>
                      <label className="new-hrm-profile-field">
                        <span>Country</span>
                        <input value={address.country || ""} onChange={(event) => updateAddress(index, "country", event.target.value)} />
                      </label>
                      <label className="new-hrm-profile-field">
                        <span>Street</span>
                        <input value={address.line1} onChange={(event) => updateAddress(index, "line1", event.target.value)} />
                      </label>
                      <label className="new-hrm-profile-field">
                        <span>City</span>
                        <input value={address.city || ""} onChange={(event) => updateAddress(index, "city", event.target.value)} />
                      </label>
                      <label className="new-hrm-profile-field">
                        <span>State</span>
                        <input value={address.state || ""} onChange={(event) => updateAddress(index, "state", event.target.value)} />
                      </label>
                      <label className="new-hrm-profile-field">
                        <span>Zip Code</span>
                        <input value={address.zipCode || ""} onChange={(event) => updateAddress(index, "zipCode", event.target.value)} />
                      </label>
                    </>
                  ) : (
                    <>
                      <strong>{normalizeAddressType(address.type)}</strong>
                      <span>{address.line1}</span>
                      <small>{[address.city, address.state, address.zipCode].filter(Boolean).join(", ")}</small>
                      <small>{address.country}</small>
                    </>
                  )}
                </article>
              ))}
            </div>
            {!addresses.length && <p className="new-hrm-empty-state">No addresses available from CRM.</p>}
          </div>
        </section>

        <section aria-label="Documents" className="new-hrm-panel new-hrm-screen-panel-span new-hrm-profile-collapsible-card new-hrm-loader-host">
          <ComponentLoader active={savingSection === "documents"} label="Updating documents" />
          <ProfileSectionHeader
            canEdit={false}
            title="Documents"
            note={documentUploadEnabled ? "Upload enabled documents only. Existing files can be replaced or deleted." : "Submitted documents from CRM."}
            collapsed={isSectionCollapsed("documents")}
            onToggle={() => toggleSectionCollapsed("documents")}
          />
          <div className="new-hrm-collapsible-content" hidden={isSectionCollapsed("documents")}>
            {customDocumentsAllowed && onDocumentUpload && (
              <button className="new-hrm-secondary-button new-hrm-profile-add-button new-hrm-profile-add-document-button" type="button" onClick={addCustomDocumentRow}>
                <FiPlus aria-hidden="true" />
                Add Document
              </button>
            )}
            <div className="new-hrm-profile-table new-hrm-profile-table--documents">
              <div className="new-hrm-profile-table__head new-hrm-profile-table__head--documents">
                <span>Document</span>
                <span>Status</span>
                <span>File</span>
                <span>Actions</span>
              </div>
              {documentRows.map((row) => (
                <div key={row.key} className="new-hrm-profile-table__row new-hrm-profile-table__row--documents">
                  {row.custom ? (
                    <label className="new-hrm-profile-mobile-field">
                      <span>Document</span>
                      <input
                        aria-label="Custom document name"
                        className="new-hrm-profile-document-name-input"
                        placeholder="Document name"
                        value={row.documentName}
                        onChange={(event) => updateCustomDocumentName(row.key, event.target.value)}
                      />
                    </label>
                  ) : (
                    <span data-label="Document">{row.documentName}</span>
                  )}
                  <em data-label="Status" className={`new-hrm-status-pill ${row.uploaded ? "new-hrm-status-pill--green" : "new-hrm-status-pill--muted"}`}>
                    {row.status}
                  </em>
                  <span data-label="File">
                    {row.downloadUrl ? (
                      <a href={row.downloadUrl} target="_blank" rel="noreferrer">
                        {row.document?.label || row.documentName}
                      </a>
                    ) : (
                      row.uploaded ? row.document?.label || row.documentName : "Not uploaded"
                    )}
                  </span>
                  <div className="new-hrm-profile-document-actions" data-label="Actions">
                    {row.downloadUrl && (
                      <a className="new-hrm-secondary-button" href={row.downloadUrl} target="_blank" rel="noreferrer">
                        <FiDownload aria-hidden="true" />
                        View
                      </a>
                    )}
                    {row.canUpload && onDocumentUpload && (
                      <label className={`new-hrm-secondary-button new-hrm-profile-upload-button ${savingSection === "documents" ? "new-hrm-button-disabled" : ""}`}>
                        <FiUpload aria-hidden="true" />
                        Upload
                        <input
                          aria-label={`Upload ${row.documentName}`}
                          disabled={savingSection === "documents"}
                          type="file"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            handleDocumentUpload(row.documentName, file, row.custom ? row.key : "");
                          }}
                        />
                      </label>
                    )}
                    {row.custom && (
                      <button
                        className="new-hrm-profile-remove-button"
                        type="button"
                        onClick={() => removeCustomDocumentRow(row.key)}
                        aria-label="Remove custom document row"
                        title="Remove custom document row"
                        disabled={savingSection === "documents"}
                      >
                        <FiTrash2 aria-hidden="true" />
                      </button>
                    )}
                    {row.canDelete && onDocumentDelete && (
                      <button
                        className="new-hrm-profile-remove-button"
                        type="button"
                        onClick={() => handleDocumentDelete(row)}
                        aria-label={`Delete ${row.documentName}`}
                        title={`Delete ${row.documentName}`}
                        disabled={savingSection === "documents"}
                      >
                        <FiTrash2 aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {!documentRows.length && <p className="new-hrm-empty-state">No submitted documents available from CRM.</p>}
          </div>
        </section>
      </fieldset>
    </section>
  );
};

export default ProfileScreen;
